import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { BottomSheet, InputText, Button, Modal, LoadingBubble } from '@encarpkg/design';
import { testNumber, testMobile } from '@encarpkg/shared';
import { getPhoneBlockInfo, getInquiryAgreements } from '@encarpkg/api';
import useCars from 'app/hook/cars/useCars';
import useInquiry from 'app/hook/buyInquiry/useInquiry';
import useEnlog from 'app/hook/common/useEnlog';
import useSsrCookie from 'app/hook/common/useSsrCookie';
import { loggingException } from 'app/libs/sentry';
import styles from './LayerPhoneInfo.module.scss';

const cx = classNames.bind(styles);

function LayerPhoneInfo({ openCertificationLayer, onSubmit, onClose }) {
	const [isOpen, setIsOpen] = useState(true);
	const [phoneValidation, setPhoneValidation] = useState(false);
	const [phoneValidationText, setPhoneValidationText] = useState('');
	const [loading, setLoading] = useState(false);
	const [isBlockedSellerModal, setIsBlockedSellerModal] = useState(false);
	const [isBlockedBySellerModal, setIsBlockedBySellerModal] = useState(false);

	const { getCookie } = useSsrCookie();
	const pcId = getCookie('PCID') || '';

	const { base } = useCars();
	const { phoneNumber, changeComm } = useInquiry();
	const { enloggingJsonData } = useEnlog();

	useEffect(() => {
		if (isOpen) {
			const dataTalkViewSingle = {
				eventNameGroup: '전화번호입력',
			};
			enloggingJsonData({
				dataTalkViewSingle,
			});
		}
	}, [enloggingJsonData, isOpen]);

	useEffect(() => {
		if (isBlockedSellerModal || isBlockedBySellerModal) {
			enloggingJsonData({
				dataTalkViewSingle: {
					eventNameGroup: isBlockedSellerModal ? '차단한상태' : '차단당한상태',
				},
			});
		}
	}, [enloggingJsonData, isBlockedBySellerModal, isBlockedSellerModal]);

	const handlePhone = (e) => {
		const { value } = e.target;
		if (value === '' || testNumber(value)) {
			changeComm({ name: 'phoneNumber', value });
		}
	};

	const isValidPhoneNumber = (v) => {
		if (v.length < 10 || !testMobile(v)) {
			setPhoneValidation(true);
			setPhoneValidationText('번호 형식이 올바르지 않습니다.');
			return false;
		}
		setPhoneValidation(false);
		setPhoneValidationText('');
		return true;
	};

	const handleConfirm = async () => {
		if (!isValidPhoneNumber(phoneNumber)) return;
		try {
			setLoading(true);
			// 구매문의 차단 여부 검증
			const { data } = await getPhoneBlockInfo(phoneNumber, '', base.vehicleId);
			// 판매자를 차단한 경우 or 서로 차단한 경우
			if (data.blocked) {
				setIsOpen(false);
				setIsBlockedSellerModal(true);
				return;
			}
			// 판매자에게 차단당한 경우
			if (data.amiBlocked) {
				setIsOpen(false);
				setIsBlockedBySellerModal(true);
				return;
			}

			// 개인정보 동의내역 조회
			const { data: agreementsResp } = await getInquiryAgreements(phoneNumber, pcId);
			// 약관동의 내역이 없다면 약관동의를 위한 휴대폰 인증 레이어 노출
			if (!agreementsResp.length) {
				openCertificationLayer();
				return;
			}
			// 판매자 메세지 발송
			await onSubmit({ eventNameGroup: '전화번호입력' });
			setLoading(false);
		} catch (e) {
			loggingException(e);
			setLoading(false);
		}
	};

	return (
		<>
			<BottomSheet
				isOpen={isOpen}
				onClose={onClose}
				hasCloseBtn
				customStyles={{ padding: '20px' }}
				header={
					<strong className={cx('tit')}>
						휴대전화번호를 남겨주시면
						<br />
						최대한 빨리 답변 드릴게요
					</strong>
				}
				data-enlog-dt-eventnamegroup="전화번호입력"
			>
				{loading && <LoadingBubble />}
				<InputText
					label="휴대전화번호"
					type="tel"
					id="inputPhone"
					name="inputPhone"
					value={phoneNumber}
					placeholder="'-'제외하고 숫자만 입력"
					maxLength={11}
					onChange={handlePhone}
					validationText={phoneValidationText}
					validation={phoneValidation}
					useLabel
					labelStyle={{ lineHeight: '19px' }}
					data-enlog-dt-eventnamegroup="전화번호입력"
				/>
				<Button.V2
					domType="button"
					color="primary"
					size={50}
					style={{ marginTop: 20 }}
					disabled={!phoneNumber}
					onClick={handleConfirm}
					data-enlog-dt-eventnamegroup="전화번호입력"
				>
					휴대전화번호 입력하기
				</Button.V2>
			</BottomSheet>

			{isBlockedSellerModal && (
				<Modal
					isOpen
					dimClosable={false}
					onClose={() => setIsBlockedSellerModal(false)}
					onClickOk={() => setIsBlockedSellerModal(false)}
					okText="확인"
					subtitle={
						<p style={{ fontSize: '16px', fontWeight: '600' }}>
							차단한 상대에게는
							<br />
							메시지 발송이 불가합니다.
						</p>
					}
					data-enlog-dt-eventnamegroup="차단한상태"
				/>
			)}

			{isBlockedBySellerModal && (
				<Modal
					isOpen
					dimClosable={false}
					onClose={() => setIsBlockedBySellerModal(false)}
					onClickOk={() => setIsBlockedBySellerModal(false)}
					okText="확인"
					subtitle={
						<p style={{ fontSize: '16px', fontWeight: '600' }}>
							판매자에게 차단되어
							<br />
							메시지 발송이 불가합니다.
						</p>
					}
					data-enlog-dt-eventnamegroup="차단당한상태"
				/>
			)}
		</>
	);
}

export default LayerPhoneInfo;
